'use client'

import { useRouter } from 'next/navigation'
import { useTransition } from 'react'

import { useLocale as nextIntlUseLocale, useTranslations } from 'next-intl'

import { useAdminStore } from '@/lib/auth/admin-store'
import { getQueryClient } from '@/query-client'
import { useUpdateLanguageMutation } from '@/query/auth/auth-mutations'

export type SupportedLocale = 'ar' | 'en'

export function useLocale() {
  const admin = useAdminStore((state) => state.admin)
  const setAdmin = useAdminStore((state) => state.setAdmin)
  const queryClient = getQueryClient()
  const locale = nextIntlUseLocale() as SupportedLocale
  const t = useTranslations('components.localeSwitch')
  const router = useRouter()
  const [isTransitionPending, startTransition] = useTransition()
  const { isPending: isUpdateLanguagePending, mutateAsync: updateLanguage } =
    useUpdateLanguageMutation()
  const direction = locale === 'ar' ? 'rtl' : 'ltr'
  const isRTL = locale === 'ar'
  const supportedLocales: { key: SupportedLocale; label: string }[] = [
    { key: 'ar', label: t('arabic') },
    { key: 'en', label: t('english') }
  ]
  const label = supportedLocales.find((l) => l.key === locale)?.label || locale

  const applyLocale = (newLocale: SupportedLocale) => {
    startTransition(() => {
      queryClient.invalidateQueries()
      document.cookie = `NEXT_LOCALE=${newLocale}; path=/; max-age=31536000; SameSite=lax`
      router.refresh()
    })
  }

  const setLocale = async (newLocale: SupportedLocale) => {
    if (newLocale === locale) return

    if (!admin) {
      applyLocale(newLocale)
      return
    }

    try {
      const response = await updateLanguage({ language: newLocale })

      if (response.success && response.data) {
        setAdmin(response.data)
        applyLocale(newLocale)
      }
    } catch {
      // Toast/error handling is delegated to the mutation/query client.
    }
  }

  return {
    direction: direction as 'ltr' | 'rtl',
    isPending: isTransitionPending || isUpdateLanguagePending,
    isRTL,
    label,
    locale,
    setLocale,
    supportedLocales
  }
}
